import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useUploadQueueContext } from "./UploadQueueContext.tsx";
import { describeTimeLeft, summarizeQueue, UploadQueueRow } from "./UploadQueueList.tsx";
import type { UploadQueueStatus } from "./useUploadQueue.ts";

export const SHARE_DOCUMENTS_PATH = '/share-documents';
const MAX_LISTED_ITEMS = 50;

const listOrder: Record<UploadQueueStatus, number> = {
    failed: 0,
    uploading: 1,
    queued: 2,
    done: 3,
};

export function UploadQueuePanel() {
    const queue = useUploadQueueContext();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    if (!queue || queue.items.length === 0 || location.pathname.startsWith(SHARE_DOCUMENTS_PATH)) return null;

    const { items, secondsLeft, isStalled } = queue;
    const { doneCount, failedItems, settledCount, isRunning } = summarizeQueue(items);
    const listedItems = [...items]
        .sort((first, second) => listOrder[first.status] - listOrder[second.status])
        .slice(0, MAX_LISTED_ITEMS);

    const title = isRunning
        ? (isStalled ? `Verbinding verloren · ${settledCount} van ${items.length}` : `Uploaden… ${settledCount} van ${items.length}`)
        : failedItems.length > 0
            ? `${doneCount} van ${items.length} gedeeld · ${failedItems.length} mislukt`
            : `${doneCount} ${doneCount === 1 ? 'document' : 'documenten'} gedeeld`;
    const subtitle = isRunning && !isStalled ? describeTimeLeft(secondsLeft) : null;

    return (
        <section aria-label="Uploads" className="fixed bottom-6 right-4 z-10 w-[calc(100vw-2rem)] max-w-96 sm:right-24 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-4 py-3 text-gray-900">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{title}</div>
                    {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
                </div>
                <button type="button" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} aria-expanded={!isCollapsed}
                        className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700" onClick={() => setIsCollapsed(!isCollapsed)}>
                    <ChevronDownIcon className={`size-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}/>
                </button>
                {!isRunning && (
                    <button type="button" aria-label="Sluiten" className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700" onClick={queue.clearFinished}>
                        <XMarkIcon className="size-5"/>
                    </button>
                )}
            </div>
            {isRunning && (
                <div className="h-1 bg-gray-200" role="progressbar" aria-label="Uploadvoortgang"
                     aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={settledCount}>
                    <div className={`h-full transition-[width] duration-300 ${isStalled ? 'bg-amber-400' : 'bg-primary'}`}
                         style={{ width: `${Math.round((settledCount / items.length) * 100)}%` }}/>
                </div>
            )}
            {!isCollapsed && (
                <>
                    {((isRunning && isStalled) || (!isRunning && failedItems.length > 0)) && (
                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2 text-sm">
                            <span className="text-gray-500">{isRunning ? 'De rest wacht tot de verbinding terug is.' : 'Niet alles is gelukt.'}</span>
                            <button type="button" className="shrink-0 whitespace-nowrap font-medium text-primary hover:underline"
                                    onClick={isRunning ? queue.resume : queue.retryAllFailed}>Opnieuw proberen</button>
                        </div>
                    )}
                    <ul className="max-h-72 divide-y divide-gray-100 overflow-y-auto">
                        {listedItems.map((item) => <UploadQueueRow key={item.id} item={item} onRetry={queue.retry}/>)}
                    </ul>
                    <Link to={SHARE_DOCUMENTS_PATH} className="block border-t border-gray-100 px-4 py-2 text-sm font-medium text-primary hover:underline">
                        {items.length > MAX_LISTED_ITEMS ? `Alle ${items.length} bestanden bekijken` : 'Naar Documenten delen'}
                    </Link>
                </>
            )}
        </section>
    );
}
